import React, { useEffect, useState } from "react";
import {
  EventPayload,
  EventTypes,
  CanvasDataEvent,
  cameraOrbit,
  cameraTarget,
} from "../../providers/types";
import { useWindowDimensions } from "../../hooks/useWindowDimensions";
import { useAppContext } from "../../contexts/appContext";
import { CanvasMode, State } from "../../contexts/types";
import { isThreeDeeEventCheck } from "../../utils/eventUtils";
import SimpleModelViewer from "./modelViewer";

type ThreeDeeViewerProps = {
  sourceUrl: string;
  leader: boolean;
  scale: number;
  sendEvent?:
    | ((eventPayload: EventPayload, appState: State) => void)
    | undefined;
  events?: CanvasDataEvent[];
  mode: CanvasMode;
};

const ThreeDeeViewer: React.FC<ThreeDeeViewerProps> = ({
  sourceUrl,
  leader,
  scale,
  sendEvent,
  mode,
  events = [],
}: ThreeDeeViewerProps) => {
  const { state } = useAppContext();
  const { height } = useWindowDimensions();
  const [receivedPosition, setReceivedPosition] = useState<any | undefined>();

  useEffect(() => {
    // Students positions from events as well as home and zoom for instructors
    if (events?.length) {
      const canvasDataEv = events[events.length - 1];
      const eventPayload: EventPayload = JSON.parse(canvasDataEv.event);

      if (
        isThreeDeeEventCheck(eventPayload) &&
        !state.exploreMode &&
        !canvasDataEv.isSelf
      ) {
        if (eventPayload) {
          switch (eventPayload.type) {
            case EventTypes.THREE_DEE_DRAG:
              setReceivedPosition(eventPayload.threeDeeData);

              break;
            case EventTypes.THREE_DEE_HOME:
              setReceivedPosition(eventPayload.threeDeeData);
              break;
          }
        }
      }
    }
  }, [events.length, leader, state.exploreMode, events]);

  const sendThreeDeeView = (
    x: number,
    y: number,
    z: number,
    leader: boolean,
    exploreMode: boolean,
    cameraOrbit?: cameraOrbit,
    cameraTarget?: cameraTarget,
    fieldOfView?: number
  ) => {
    if (leader && !exploreMode && sendEvent) {
      sendEvent(
        {
          type: EventTypes.THREE_DEE_DRAG,
          leader: leader,
          threeDeeData: {
            x: x,
            y: y,
            z: z,
            cameraOrbit: cameraOrbit,
            cameraTarget: cameraTarget,
            fieldOfView: fieldOfView,
          },
          exploreMode: exploreMode,
        },
        state
      );
    }
  };
  const handleCameraChange = (position: any) => {
    // Use a throttle to avoid sending too many updates
    let timeoutId;
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      sendThreeDeeView(
        position.azimuthAngle, // azimuthAngle
        position.polarAngle, // polarAngle
        position.fov, // distance
        leader, // leader
        state.exploreMode, // exploreMode
        position?.cameraOrbit,
        position?.cameraTarget,
        position?.fieldOfView
      );
    }, 500); // Send updates every 100ms
  };
  return (
    <div
      style={{ height: height, touchAction: "none", cursor: "grab" }}
      id="threeDeeViewer"
      tabIndex={0}
    >
      <SimpleModelViewer
        src={sourceUrl}
        leader={leader}
        onCameraChange={handleCameraChange}
        receivedCameraPosition={receivedPosition}
        isExploreMode={state.exploreMode}
        events={events}
        mode={mode}
      />
    </div>
  );
};

export default ThreeDeeViewer;
